import React, { useMemo, useState } from 'react';
import { useAppState } from '../libraries/AppContext';
import LoadingSpinner from './LoadingSpinner';

interface ChatPageProps {
    onConversationClick: (conversationId: number) => void;
    onStartChat: (participantId: number) => Promise<boolean>;
    isCreateOpen: boolean;
    onCloseCreate: () => void;
    startingChatParticipantId: number | null;
}

const ChatPage: React.FC<ChatPageProps> = ({
    onConversationClick,
    onStartChat,
    isCreateOpen,
    onCloseCreate,
    startingChatParticipantId
}) => {
    const { eventData } = useAppState();
    const [searchTerm, setSearchTerm] = useState('');
    const blockedParticipantIds = useMemo(() => {
        if (!eventData) return new Set<number>();
        return new Set(
            eventData.participants
                .filter(p => p.isBlocked)
                .map(p => p.id)
        );
    }, [eventData]);
    // Filter out conversations without messages
    const conversations = (eventData?.chat_conversations || [])
        .filter(c => c.lastMessage)
        .filter(c => !blockedParticipantIds.has(c.otherParticipant.id));
    const myParticipantId = useMemo(() => {
        if (!eventData) return null;
        const myUser = eventData.users.find(u => u.id === eventData.me.id);
        return myUser?.participantId ?? null;
    }, [eventData]);
    const filteredParticipants = useMemo(() => {
        if (!eventData) return [];
        const term = searchTerm.trim().toLowerCase();
        return eventData.participants
            .filter(p => (myParticipantId ? p.id !== myParticipantId : true))
            .filter(p => !p.isBlocked)
            .filter(p => {
                if (!term) return true;
                const haystack = `${p.vorname} ${p.nachname} ${p.email}`.toLowerCase();
                return haystack.includes(term);
            })
            .sort((a, b) => {
                const nameA = `${a.nachname} ${a.vorname}`.toLowerCase();
                const nameB = `${b.nachname} ${b.vorname}`.toLowerCase();
                return nameA.localeCompare(nameB);
            });
    }, [eventData, myParticipantId, searchTerm]);

    const handleStartChat = async (participantId: number) => {
        const started = await onStartChat(participantId);
        if (started) {
            onCloseCreate();
            setSearchTerm('');
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden h-full flex flex-col flex-1 min-h-0">
            {isCreateOpen && (
                <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 px-4" onClick={() => {
                    if (startingChatParticipantId !== null) return;
                    onCloseCreate();
                    setSearchTerm('');
                }}>
                    <div
                        role="dialog"
                        aria-modal="true"
                        className="w-full max-w-md rounded-xl bg-white shadow-lg border border-gray-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                            <h2 className="text-base font-semibold text-gray-900">Neuen Chat starten</h2>
                            <button
                                type="button"
                                onClick={() => {
                                    if (startingChatParticipantId !== null) return;
                                    onCloseCreate();
                                    setSearchTerm('');
                                }}
                                className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                disabled={startingChatParticipantId !== null}
                            >
                                Schließen
                            </button>
                        </div>
                        <div className="p-4">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Teilnehmer suchen..."
                                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-pink-500 focus:ring focus:ring-pink-200 focus:ring-opacity-50"
                            />
                            <div className="mt-3 max-h-64 overflow-y-auto overflow-x-hidden">
                                {filteredParticipants.length === 0 ? (
                                    <p className="text-sm text-gray-500">Keine Teilnehmer gefunden.</p>
                                ) : (
                                    <ul className="divide-y divide-gray-100">
                                        {filteredParticipants.map(p => (
                                            <li key={p.id}>
                                        <button
                                            type="button"
                                            onClick={() => handleStartChat(p.id)}
                                            disabled={startingChatParticipantId !== null}
                                            className="w-full text-left px-2 py-2 hover:bg-gray-50 rounded-md flex items-center justify-between disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            <span className="text-sm text-gray-900">
                                                {p.vorname} {p.nachname}
                                            </span>
                                            {startingChatParticipantId === p.id && (
                                                <LoadingSpinner size="sm" className="ml-2" />
                                            )}
                                        </button>
                                        <div className="px-2 pb-2 text-xs text-gray-500">{p.standort}</div>
                                    </li>
                                ))}
                            </ul>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {conversations.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    <p>Sie haben noch keine Nachrichten.</p>
                    <p className="text-sm mt-2">Starten Sie einen Chat über die Teilnehmerliste.</p>
                </div>
            ) : (
                <ul className="divide-y divide-gray-100 overflow-y-auto flex-1 min-h-0">
                    {conversations.map((conversation) => (
                        <li
                            key={conversation.id}
                            onClick={() => onConversationClick(conversation.id)}
                            className="hover:bg-gray-50 cursor-pointer transition-colors p-4 flex items-center gap-4"
                        >
                            {/* Profile Image */}
                            <div className="relative">
                                {conversation.otherParticipant.profileImage ? (
                                    <img
                                        src={conversation.otherParticipant.profileImage}
                                        alt={conversation.otherParticipant.vorname}
                                        className="w-12 h-12 rounded-full object-cover border border-gray-200"
                                    />
                                ) : (
                                    <div className="w-12 h-12 rounded-full bg-linear-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                                        {conversation.otherParticipant.vorname.charAt(0)}
                                        {conversation.otherParticipant.nachname.charAt(0)}
                                    </div>
                                )}
                                {/* Unseen badge */}
                                {conversation.unseenCount !== undefined && conversation.unseenCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-5 h-5 flex items-center justify-center px-1 shadow-md">
                                        {conversation.unseenCount > 99 ? '99+' : conversation.unseenCount}
                                    </span>
                                )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-baseline mb-1">
                                    <h3 className="text-base font-semibold text-gray-900 truncate">
                                        {conversation.otherParticipant.vorname} {conversation.otherParticipant.nachname}
                                    </h3>
                                    {conversation.lastMessage && (
                                        <span className="text-xs text-gray-500">
                                            {new Date(conversation.lastMessage.createdAt).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600 truncate">
                                    {conversation.lastMessage ? conversation.lastMessage.content : <i>Keine Nachrichten</i>}
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default ChatPage;
